import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronRight,
  Trash2,
  Plus,
  X,
  Edit2,
  Loader2,
  AlertTriangle,
} from "lucide-react";

import { ocrApi } from "../../../services/ocrApi";
import { WorkflowAssignModal } from "./WorkflowAssignModal";

export type RulesViewerTab = "lineitem" | "fielditem";

const FIELD_BOUNDARY_TYPES: { label: string; value: string }[] = [
  { label: "Single Box", value: "KEY_AND_VALUE_SINGLE" },
  { label: "Separate Box", value: "KEY_AND_VALUE_SEPARATE" },
  { label: "Static Value", value: "ONLY_VALUE" },
];

interface Coordinates {
  xmin: string;
  ymin: string;
  xmax: string;
  ymax: string;
  xDif: string;
  yDif: string;
}

interface FieldItem {
  id: number;
  field_name: string;
  pageNumber: string;
  boundaryType: string;
  keywords: string[];
  value_coordinates: Coordinates;
  key_coordinates: Coordinates;
}

/**
 * Per-tab LLM configuration. Mirrors the backend layout-detail shape
 * (`llm.lineitem_config` / `llm.field_config`). All four fields must be
 * preserved on retrigger — `verification_retriger_lineitem` reads
 * `model_name` to pick the model and `prompt` / `prompt_message` for the
 * baseline template. Sending empty strings would force the backend onto
 * the wrong defaults and the LLM extraction silently no-ops.
 */
interface LlmTabConfig {
  model_name: string;
  prompt: string;
  user_prompt: string;
  prompt_message: string;
}

interface LayoutData {
  layout_id: string;
  layout_name: string;
  workflow_id: string;
  field_extraction_type: "rules" | "llm";
  lineitem_extraction_type: "rules" | "llm";
  /** LLM-mode configuration. Only used when an extraction_type === "llm". */
  llm: {
    lineitem_config: LlmTabConfig;
    field_config: LlmTabConfig;
  };
  /** Line-item rule structure (mirrors ocr-ui's LineItemsTab). */
  lineItem: LineItemRule;
}

const EMPTY_LLM_CONFIG: LlmTabConfig = {
  model_name: "",
  prompt: "",
  user_prompt: "",
  prompt_message: "",
};

interface ColumnBoundary {
  xmin: string;
  xmax: string;
  ymin: string;
  ymax: string;
}

interface LineItemRule {
  columnSegmentation: string;
  lineItemSplitting: string;
  selectedColumn: string;
  headerIdentification: string;
  footerIdentification: string;
  /** Ordered map: column name → boundary. */
  columnBoundary: Record<string, ColumnBoundary>;
  headerKeywords: string[][];
  footerKeywords: string[];
}

const DEFAULT_LINE_ITEM_RULE: LineItemRule = {
  // First-time defaults shown to a reviewer who hasn't picked anything
  // yet. Layout-detail responses still override these via parseLineItemRule
  // — these only kick in when the backend returns no saved selection.
  columnSegmentation: "keyword",
  lineItemSplitting: "index",
  selectedColumn: "",
  headerIdentification: "keyword",
  footerIdentification: "keyword",
  columnBoundary: {},
  headerKeywords: [],
  footerKeywords: [],
};

const COLUMN_SEGMENTATION_OPTIONS = [
  { label: "Select an option", value: "" },
  { label: "Keyword", value: "keyword" },
  { label: "Line", value: "line" },
  { label: "Rule", value: "rule" },
  { label: "Space", value: "space" },
];

const LINEITEM_SPLITTING_OPTIONS = [
  { label: "Select an option", value: "" },
  { label: "Max Pattern", value: "max_pattern" },
  { label: "Index", value: "index" },
  { label: "Rule", value: "rule" },
];

const IDENTIFICATION_OPTIONS = [
  { label: "Keyword", value: "keyword" },
  { label: "Pattern", value: "pattern" },
];

const EMPTY_COORDS: Coordinates = {
  xmin: "",
  ymin: "",
  xmax: "",
  ymax: "",
  xDif: "",
  yDif: "",
};

/**
 * Merge backend-supplied dropdown options with a local fallback set,
 * de-duped by `value`. Backend entries win on label (their casing /
 * translation is preferred) but any missing fallback values are
 * appended so the default selected value is guaranteed to render.
 */
function mergeOptions(
  fromBackend: { label: string; value: string }[] | undefined,
  fallback: { label: string; value: string }[],
): { label: string; value: string }[] {
  if (!fromBackend || fromBackend.length === 0) return fallback;
  const seen = new Set(fromBackend.map((o) => o.value));
  const merged = [...fromBackend];
  for (const o of fallback) {
    if (!seen.has(o.value)) {
      merged.push(o);
      seen.add(o.value);
    }
  }
  return merged;
}

interface LayoutRulesViewerProps {
  layoutId: string;
  defaultTab?: RulesViewerTab;
  /** Sync the parent's active extraction tab when the user flips tabs here. */
  onTabChange?: (tab: RulesViewerTab) => void;
  onCancel: () => void;
  /** Optional already-fetched coordinates payload from the parent's
   *  `coordinatesQuery`. When provided, used as a primary source for
   *  field-item rows (skips an extra round-trip and stays in sync with
   *  the document overlay). */
  coordinatesData?: unknown;
  /** Defaults sourced from the parent (URL params + verification_home).
   *  Used to populate the Edit layout form when the layout-detail call
   *  doesn't carry these fields. */
  initialWorkflowId?: string;
  initialLayoutName?: string;
  /** Process ID — required by the Retrigger endpoint
   *  (`/verification_retriger_lineitem`) to re-process the active doc. */
  processId?: string;
  /**
   * Notify the parent that a retrigger succeeded so it can refresh the
   * extracted-data side panel. The WebSocket `process_complete` frame
   * normally handles this, but the eager callback covers the case where
   * the frame is delayed or never arrives.
   *
   * Can return a Promise — when it does, the dialog stays open in its
   * "Retriggering…" loading state until the promise resolves. This lets
   * the parent await its own refetches (projection / coordinates) so
   * the reviewer doesn't get dumped back onto a stale results view.
   */
  onRetriggerSuccess?: () => void | Promise<void>;
}

function coordArrayToObj(arr: unknown): Coordinates {
  if (!Array.isArray(arr)) return { ...EMPTY_COORDS };
  return {
    xmin: arr[0] != null ? String(arr[0]) : "",
    ymin: arr[1] != null ? String(arr[1]) : "",
    xmax: arr[2] != null ? String(arr[2]) : "",
    ymax: arr[3] != null ? String(arr[3]) : "",
    xDif: arr[4] != null ? String(arr[4]) : "",
    yDif: arr[5] != null ? String(arr[5]) : "",
  };
}

/**
 * Converts a `select_coordinates` cell — `{ xmin, ymin, xmax, ymax }` — into
 * the editable coordinate shape. xDif/yDif are not reported by the
 * coordinates endpoint so they're left blank.
 */
function coordObjToObj(raw: unknown): Coordinates {
  if (!raw || typeof raw !== "object") return { ...EMPTY_COORDS };
  const o = raw as Record<string, unknown>;
  const f = (k: string) => (o[k] != null ? String(o[k]) : "");
  return {
    xmin: f("xmin"),
    ymin: f("ymin"),
    xmax: f("xmax"),
    ymax: f("ymax"),
    xDif: "",
    yDif: "",
  };
}

/**
 * Pulls line-item rule fields out of a LayoutDetail response. Mirrors
 * the `delimiter_config` mapping in `LayoutEditor.refreshData`. Also
 * accepts the `/select_coordinates` response as a fallback source for
 * column boundaries (derived from `header.vertical_line` field_name +
 * xmin pairs) when the layout-detail call doesn't carry them.
 */
function parseLineItemRule(
  d: Record<string, unknown>,
  coordsData?: Record<string, unknown> | null,
): LineItemRule {
  const delim =
    (d.delimiter_config as Record<string, unknown> | undefined) ??
    ((d.rules as Record<string, unknown> | undefined)
      ?.delimiter_config as Record<string, unknown> | undefined) ??
    {};
  const lineItemRules = (Array.isArray(d.line_item_rules)
    ? (d.line_item_rules as Record<string, unknown>[])
    : []);
  const firstRule = lineItemRules[0] ?? {};

  const header = (delim.header as Record<string, unknown> | undefined) ?? {};
  const footer = (delim.footer as Record<string, unknown> | undefined) ?? {};
  const column = (delim.column as Record<string, unknown> | undefined) ?? {};
  const lineitem =
    (delim.lineitem as Record<string, unknown> | undefined) ?? {};

  // Column boundaries: `{ "Description": {xmin, xmax}, ... }`.
  const columnBoundaryRaw =
    (column.column_boundary as Record<string, unknown> | undefined) ?? {};
  let columnBoundary: Record<string, ColumnBoundary> = {};
  for (const [name, raw] of Object.entries(columnBoundaryRaw)) {
    const r = (raw as Record<string, unknown> | undefined) ?? {};
    columnBoundary[name] = {
      xmin: r.xmin != null ? String(r.xmin) : "",
      xmax: r.xmax != null ? String(r.xmax) : "",
      ymin: r.ymin != null ? String(r.ymin) : "",
      ymax: r.ymax != null ? String(r.ymax) : "",
    };
  }

  // Fallback: derive column boundaries from
  // `lineitem_raw_coordinates.coordinates.header.vertical_line[]` when
  // `delimiter_config.column.column_boundary` is missing. For N columns
  // there are N+1 vertical lines; each line i (sorted by xmin) names
  // and starts column i, and line i+1 ends it.
  if (Object.keys(columnBoundary).length === 0) {
    const liRoot = (coordsData?.lineitem_raw_coordinates as
      | Record<string, unknown>
      | undefined) ?? (d.lineitem_raw_coordinates as
      | Record<string, unknown>
      | undefined);
    const liCoords =
      (liRoot?.coordinates as Record<string, unknown> | undefined) ?? liRoot;
    const liHeader = (liCoords as Record<string, unknown> | undefined)
      ?.header as Record<string, unknown> | undefined;
    const vLinesRaw = liHeader?.vertical_line;
    if (Array.isArray(vLinesRaw) && vLinesRaw.length >= 2) {
      const sorted = [...vLinesRaw]
        .filter(
          (l) =>
            Number.isFinite(Number((l as Record<string, unknown>)?.xmin)),
        )
        .sort(
          (a, b) =>
            Number((a as Record<string, unknown>).xmin) -
            Number((b as Record<string, unknown>).xmin),
        );
      const derived: Record<string, ColumnBoundary> = {};
      for (let i = 0; i < sorted.length - 1; i++) {
        const cur = sorted[i] as Record<string, unknown>;
        const next = sorted[i + 1] as Record<string, unknown>;
        const fieldName =
          typeof cur.field_name === "string" && cur.field_name
            ? (cur.field_name as string)
            : `Column ${i + 1}`;
        derived[fieldName] = {
          xmin: cur.xmin != null ? String(cur.xmin) : "",
          xmax: next.xmin != null ? String(next.xmin) : "",
          ymin: cur.ymin != null ? String(cur.ymin) : "",
          ymax: cur.ymax != null ? String(cur.ymax) : "",
        };
      }
      if (Object.keys(derived).length > 0) {
        columnBoundary = derived;
      }
    }
  }

  // index_column → "column_<n>" string for the selectedColumn dropdown.
  let selectedColumn = "";
  const idx = lineitem.index_column;
  if (Array.isArray(idx) && idx.length > 0) {
    selectedColumn = `column_${Number(idx[0]) + 1}`;
  } else if (typeof idx === "number") {
    selectedColumn = `column_${idx + 1}`;
  } else if (typeof idx === "string") {
    selectedColumn = idx;
  }

  const headerKeywords = Array.isArray(header.header_keywords)
    ? (header.header_keywords as unknown[]).map((row) =>
        Array.isArray(row) ? (row as unknown[]).map((s) => String(s)) : [],
      )
    : [];
  const footerKeywords = Array.isArray(footer.footer_keyword)
    ? (footer.footer_keyword as unknown[]).map((s) => String(s))
    : Array.isArray(footer.footer_keywords)
      ? (footer.footer_keywords as unknown[]).map((s) => String(s))
      : [];

  // Default selectedColumn to the LAST column option (e.g. "TOTAL") when
  // the backend hasn't saved an index_column yet. Mirrors the product
  // request: reviewers usually pick the totals column as the split
  // index, so we pre-select it. Falls back to the backend's saved value
  // when present.
  if (!selectedColumn && headerKeywords.length > 0) {
    selectedColumn = `column_${headerKeywords.length}`;
  }

  return {
    // Respect saved values from the layout-detail response. Defaults
    // ("keyword" / "index" / last column) only kick in when the
    // backend hasn't stored anything for that field — `selectedColumn`
    // already has the last-column fallback applied above.
    columnSegmentation:
      typeof firstRule.column_segmentation === "string" &&
      firstRule.column_segmentation.length > 0
        ? (firstRule.column_segmentation as string)
        : "keyword",
    lineItemSplitting:
      typeof firstRule.line_item_splitting === "string" &&
      firstRule.line_item_splitting.length > 0
        ? (firstRule.line_item_splitting as string)
        : "index",
    selectedColumn,
    headerIdentification:
      typeof firstRule.header_identification === "string"
        ? (firstRule.header_identification as string)
        : "keyword",
    footerIdentification:
      typeof firstRule.footer_identification === "string"
        ? (firstRule.footer_identification as string)
        : "keyword",
    columnBoundary,
    headerKeywords,
    footerKeywords,
  };
}

/**
 * Parses a LayoutDetail response into the editable shape we render.
 * Mirrors `LayoutEditor.refreshData` mapping in ocr-ui. Also accepts an
 * optional `coordsData` (a `/select_coordinates` response) as a fallback
 * source of field-item rows when the layout-detail call doesn't include
 * `field_rules` / `section_coordinates`.
 */
/**
 * Coerce a backend-supplied `llm.<tab>_config` object into the editor's
 * `LlmTabConfig` shape. We preserve every field — not just user_prompt —
 * because the retrigger endpoint reads `model_name`, `prompt`, and
 * `prompt_message` to pick the model and template. Discarding them would
 * force the backend onto its default model and the LLM extraction
 * silently no-ops with a generic system prompt.
 */
function parseLlmConfig(raw: unknown): LlmTabConfig {
  if (!raw || typeof raw !== "object") return { ...EMPTY_LLM_CONFIG };
  const obj = raw as Record<string, unknown>;
  const s = (v: unknown): string =>
    v == null ? "" : typeof v === "string" ? v : String(v);
  return {
    model_name: s(obj.model_name),
    prompt: s(obj.prompt),
    user_prompt: s(obj.user_prompt),
    prompt_message: s(obj.prompt_message),
  };
}

/**
 * Field-by-field merge of two LlmTabConfigs. The incoming backend value
 * is preferred when non-empty; otherwise the previous editor state is
 * kept. This matches ocr-ui's `||` fallback pattern in LayoutEditor.jsx
 * `refreshData` and keeps user-typed prompts from being clobbered by a
 * partial backend response (e.g. a retrigger refetch that hasn't
 * persisted the prompt yet).
 */
function mergeLlmConfig(prev: LlmTabConfig, next: LlmTabConfig): LlmTabConfig {
  return {
    model_name: next.model_name || prev.model_name,
    prompt: next.prompt || prev.prompt,
    user_prompt: prev.user_prompt || next.user_prompt,
    prompt_message: next.prompt_message || prev.prompt_message,
  };
}

function parseDetail(
  detail: Record<string, unknown> | null,
  coordsData?: Record<string, unknown> | null,
  fallbackLayoutId?: string,
): {
  layout: LayoutData;
  fieldItems: FieldItem[];
} {
  const d = detail ?? {};
  // Read string helper — coerces non-empty values, returns "" otherwise.
  // Empty strings from the backend behave like missing so the next
  // fallback in the chain wins.
  const str = (v: unknown): string =>
    v == null ? "" : typeof v === "string" ? v : String(v);
  const firstNonEmpty = (...candidates: unknown[]): string => {
    for (const c of candidates) {
      const s = str(c);
      if (s.length > 0) return s;
    }
    return "";
  };

  const layout: LayoutData = {
    layout_id: firstNonEmpty(d.layout_id, fallbackLayoutId),
    layout_name: firstNonEmpty(
      d.layout_name,
      d.customer_name,
      d.template_name,
      d.layout_id,
      fallbackLayoutId,
    ),
    workflow_id: firstNonEmpty(d.workflow_id, d.workflowId),
    field_extraction_type:
      (d.field_extraction_type as "llm") === "llm" ? "llm" : "rules",
    lineitem_extraction_type:
      (d.lineitem_extraction_type as "llm") === "llm" ? "llm" : "rules",
    llm: {
      lineitem_config: parseLlmConfig(
        (d.llm as Record<string, unknown> | undefined)?.lineitem_config,
      ),
      field_config: parseLlmConfig(
        (d.llm as Record<string, unknown> | undefined)?.field_config,
      ),
    },
    lineItem: parseLineItemRule(d, coordsData ?? null),
  };

  const fieldRules = Array.isArray(d.field_rules)
    ? (d.field_rules as Record<string, unknown>[])
    : [];

  const sectionCoords = (() => {
    const sc = d.section_coordinates as Record<string, unknown> | undefined;
    const p1 = sc?.["1"] as Record<string, unknown> | undefined;
    const list = p1?.field_item_raw_coordinates;
    return Array.isArray(list) ? (list as Record<string, unknown>[]) : [];
  })();

  // /select_coordinates returns { field_item_raw_coordinates: [...] } —
  // each entry has { key, value, field_name, field_keywords? }. We use
  // this as the primary source when the layout-detail call returns no
  // field_rules (which is the common case during initial extraction).
  const rawFieldCoords = (() => {
    if (!coordsData) return [];
    const list = (coordsData as Record<string, unknown>)
      .field_item_raw_coordinates;
    return Array.isArray(list) ? (list as Record<string, unknown>[]) : [];
  })();

  const names = Array.from(
    new Set([
      ...fieldRules.map((f) => String(f.field_name ?? "")),
      ...sectionCoords.map((f) => String(f.field_name ?? "")),
      ...rawFieldCoords.map((f) => String(f.field_name ?? "")),
    ]),
  ).filter((n) => n.length > 0);

  // Combined coords pool — check both the /select_coordinates response
  // and the layout-detail's section_coordinates because either may carry
  // the {key, value, field_name, field_keywords} entries depending on
  // where the layout is in its lifecycle.
  const combinedCoords = [...rawFieldCoords, ...sectionCoords];

  const fieldItems: FieldItem[] = names.map((name, index) => {
    const rule = fieldRules.find((f) => f.field_name === name) ?? {};
    const fromCoords = combinedCoords.find(
      (f) => String(f.field_name ?? "") === name,
    );

    // Keywords: prefer field_rules, then coordinates feed.
    const keywordsRaw = (rule.field_keywords ??
      rule.keywords ??
      fromCoords?.field_keywords) as unknown;
    const keywords = Array.isArray(keywordsRaw)
      ? (keywordsRaw as unknown[]).map((k) => String(k))
      : keywordsRaw
      ? [String(keywordsRaw)]
      : [];

    // Coordinates: prefer field_rules' raw arrays, fall back to the
    // {xmin,ymin,xmax,ymax} objects from /select_coordinates /
    // section_coordinates. Some payloads also use *_coordinates as
    // property aliases — try both.
    const fromValue =
      (fromCoords?.value as unknown) ??
      (fromCoords?.value_coordinates as unknown);
    const fromKey =
      (fromCoords?.key as unknown) ??
      (fromCoords?.key_coordinates as unknown);

    const valueCoords =
      rule.field_boundary !== undefined
        ? coordArrayToObj(rule.field_boundary)
        : coordObjToObj(fromValue);
    const keyCoords =
      rule.key_boundary !== undefined
        ? coordArrayToObj(rule.key_boundary)
        : coordObjToObj(fromKey);

    return {
      id: (rule.id as number) ?? index + 1,
      field_name: name,
      pageNumber: String(rule.page_no ?? rule.pageNumber ?? "1"),
      boundaryType:
        typeof rule.boundaryType === "string"
          ? (rule.boundaryType as string)
          : "KEY_AND_VALUE_SINGLE",
      keywords,
      value_coordinates: valueCoords,
      key_coordinates: keyCoords,
    };
  });

  return { layout, fieldItems };
}

export function LayoutRulesViewer({
  layoutId,
  defaultTab = "lineitem",
  onTabChange,
  onCancel,
  coordinatesData,
  initialWorkflowId,
  initialLayoutName,
  processId,
  onRetriggerSuccess,
}: LayoutRulesViewerProps) {
  const [tab, setTab] = useState<RulesViewerTab>(defaultTab);
  const [layout, setLayout] = useState<LayoutData>({
    layout_id: layoutId,
    layout_name: initialLayoutName ?? "",
    workflow_id: initialWorkflowId ?? "",
    field_extraction_type: "rules",
    lineitem_extraction_type: "rules",
    llm: {
      lineitem_config: { ...EMPTY_LLM_CONFIG },
      field_config: { ...EMPTY_LLM_CONFIG },
    },
    lineItem: { ...DEFAULT_LINE_ITEM_RULE },
  });
  const [fieldItems, setFieldItems] = useState<FieldItem[]>([]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);

  useEffect(() => {
    setTab(defaultTab);
  }, [defaultTab]);

  const detailQuery = useQuery({
    queryKey: ["ocr", "layout", "detail", layoutId],
    queryFn: () => ocrApi.getLayout(layoutId),
    enabled: !!layoutId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Field-item coordinates from /select_coordinates — primary source for
  // the Field Item editor when the layout-detail call doesn't include
  // `field_rules` (common case mid-extraction).
  const fieldCoordsQuery = useQuery({
    queryKey: ["ocr", "layout", "coords", "fielditem", layoutId],
    queryFn: () =>
      ocrApi.selectCoordinates({
        coordinatesType: "fielditem",
        layoutId,
        pageNumber: 1,
      }),
    enabled: !!layoutId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Line-item coordinates from /select_coordinates — primary source for
  // the Line Item editor's column boundaries when `delimiter_config`
  // doesn't carry them.
  const lineCoordsQuery = useQuery({
    queryKey: ["ocr", "layout", "coords", "lineitem", layoutId],
    queryFn: () =>
      ocrApi.selectCoordinates({
        coordinatesType: "lineitem",
        layoutId,
        pageNumber: 1,
      }),
    enabled: !!layoutId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Rule-delimiter dropdown options — populates header/footer
  // identification, column segmentation, and lineitem splitting selects
  // with backend-driven choices. Mirrors ocr-ui's fetchConfigOptions.
  const ruleOptionsQuery = useQuery({
    queryKey: ["ocr", "layout", "rule-delimiter-options"],
    queryFn: () => ocrApi.fetchRuleDelimiterOptions(),
    staleTime: 30 * 60_000, // 30 min — config doesn't change frequently
    refetchOnWindowFocus: false,
  });

  // Format snake_case → "Snake Case" for option labels, matching the
  // reference's `transformOptions` helper.
  const formatOptionLabel = (s: string) =>
    s
      .split("_")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");

  const ruleOptions = useMemo(() => {
    const raw = ruleOptionsQuery.data as Record<string, unknown> | undefined;
    const data =
      (raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data)
        ? (raw.data as Record<string, unknown>)
        : raw) ?? {};
    const toOpts = (
      arr: unknown,
      includeSelect: boolean,
    ): { label: string; value: string }[] => {
      const list = Array.isArray(arr) ? (arr as unknown[]) : [];
      const out = list
        .filter((v) => typeof v === "string" && (v as string).length > 0)
        .map((v) => ({
          label: formatOptionLabel(v as string),
          value: v as string,
        }));
      return includeSelect
        ? [{ label: "Select an option", value: "" }, ...out]
        : out;
    };
    return {
      headerIdentification: toOpts(data.header_identification, false),
      footerIdentification: toOpts(data.footer_identification, false),
      columnSegmentation: toOpts(data.column_segmentation, true),
      lineItemSplitting: toOpts(data.line_item_splitting, true),
    };
  }, [ruleOptionsQuery.data]);

  // Workflows for the dropdown.
  const workflowsQuery = useQuery({
    queryKey: ["ocr", "workflows"],
    queryFn: () => ocrApi.listWorkflows(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const workflowOptions = useMemo<string[]>(() => {
    const data = workflowsQuery.data as Record<string, unknown> | undefined;
    if (!data) return [];
    const wrapped = (data.data as Record<string, unknown> | undefined) ?? data;
    if (wrapped && typeof wrapped === "object") {
      return Object.keys(wrapped as Record<string, unknown>);
    }
    return [];
  }, [workflowsQuery.data]);

  // Hydrate editable state from the API responses. Re-runs when any of
  // the data sources arrive, so the editors populate as data flows in.
  useEffect(() => {
    if (
      !detailQuery.data &&
      !fieldCoordsQuery.data &&
      !lineCoordsQuery.data &&
      !coordinatesData
    )
      return;
    // Merge all available coordinate sources so parseDetail has both
    // `field_item_raw_coordinates` (for field-item rows) and
    // `lineitem_raw_coordinates` (for line-item column boundaries).
    const coords: Record<string, unknown> = {
      ...((fieldCoordsQuery.data ?? {}) as Record<string, unknown>),
      ...((lineCoordsQuery.data ?? {}) as Record<string, unknown>),
      ...((coordinatesData ?? {}) as Record<string, unknown>),
    };
    const parsed = parseDetail(
      (detailQuery.data ?? null) as Record<string, unknown> | null,
      coords,
      layoutId,
    );
    // Mirror ocr-ui's LayoutEditor.refreshData merge pattern:
    // `data.layout_id || prev.layout_id` — keep whichever value is
    // non-empty so an incomplete API response can't blank out a
    // previously-populated field.
    setLayout((prev) => ({
      layout_id: parsed.layout.layout_id || prev.layout_id || layoutId,
      layout_name: parsed.layout.layout_name || prev.layout_name,
      workflow_id: parsed.layout.workflow_id || prev.workflow_id,
      field_extraction_type:
        parsed.layout.field_extraction_type ||
        prev.field_extraction_type ||
        "rules",
      lineitem_extraction_type:
        parsed.layout.lineitem_extraction_type ||
        prev.lineitem_extraction_type ||
        "rules",
      llm: {
        // Merge field-by-field so an incomplete backend response can't
        // blank out values the user typed into the editor. The
        // user_prompt field gets special treatment — if the user has
        // typed one locally, prefer that over whatever the backend has,
        // so detailQuery refetches (e.g. after retrigger) don't wipe
        // the prompt mid-edit.
        lineitem_config: mergeLlmConfig(
          prev.llm.lineitem_config,
          parsed.layout.llm.lineitem_config,
        ),
        field_config: mergeLlmConfig(
          prev.llm.field_config,
          parsed.layout.llm.field_config,
        ),
      },
      // Replace lineItem outright — its sub-shape doesn't merge cleanly
      // and the parsed result already represents the source of truth.
      lineItem: parsed.layout.lineItem,
    }));
    setFieldItems(parsed.fieldItems);
    // Expand the first field by default — matches ocr-ui's `field1: true`.
    if (parsed.fieldItems[0]) {
      setExpanded({ [parsed.fieldItems[0].id]: true });
    }
  }, [
    detailQuery.data,
    fieldCoordsQuery.data,
    lineCoordsQuery.data,
    coordinatesData,
    layoutId,
  ]);

  // Coord helper that mirrors ocr-ui's unmapCoords: parses the
  // editable {xmin,ymin,xmax,ymax,xDif,yDif} object back to the raw
  // [x,y,w,h,xDif,yDif] tuple the backend stores.
  const unmapCoords = (c: Coordinates): number[] => [
    Math.round(parseFloat(c.xmin) || 0),
    Math.round(parseFloat(c.ymin) || 0),
    Math.round(parseFloat(c.xmax) || 0),
    Math.round(parseFloat(c.ymax) || 0),
    Math.round(parseFloat(c.xDif) || 0),
    Math.round(parseFloat(c.yDif) || 0),
  ];

  const retriggerMutation = useMutation({
    mutationFn: () => {
      if (!processId) {
        return Promise.reject(
          new Error("Process ID is required to retrigger extraction."),
        );
      }
      // LLM-mode prompts are optional — if the reviewer leaves them
      // blank the backend falls back to the saved `prompt` /
      // `prompt_message` template on the layout, so retrigger still
      // does useful work.
      // Build the layout_rule payload that
      // `/verification_retriger_lineitem` expects, mirroring ocr-ui's
      // LayoutEditor.handleProcess.
      const layoutRule: Record<string, unknown> = {
        layout_id: String(layout.layout_id),
        lineitem_extraction_type: layout.lineitem_extraction_type || "rules",
        field_extraction_type: layout.field_extraction_type || "rules",
        // Spread the FULL llm config — the backend reads model_name,
        // prompt, and prompt_message alongside user_prompt. Hardcoding
        // them to "" (as a previous version did) made the backend fall
        // back to a default model with a generic system prompt, so the
        // user's typed prompt was effectively ignored. Mirrors ocr-ui's
        // LayoutEditor.handleProcess which passes the whole config
        // through unchanged.
        llm: {
          field_config: { ...layout.llm.field_config },
          lineitem_config: { ...layout.llm.lineitem_config },
        },
        field_rules: fieldItems.map((f) => ({
          field_name: f.field_name,
          field_boundary: unmapCoords(f.value_coordinates),
          field_keywords: Array.isArray(f.keywords) ? f.keywords : [],
          key_boundary: unmapCoords(f.key_coordinates),
          page_no: String(f.pageNumber || "1"),
        })),
        // Line-item rule mirroring ocr-ui's LayoutEditor.handleProcess
        // payload (sent to /verification_retriger_lineitem). Critically,
        // the retrigger endpoint expects `index_column` as a STRING like
        // "column 1" plus an `index_column_keyword` — different from the
        // save endpoint which uses an array of indices. Matches reference
        // exactly so the backend's pydantic schema accepts the body.
        delimiter_config: {
          header: {
            header_keywords: layout.lineItem.headerKeywords,
            header_boundary: [],
            field_names: [],
          },
          footer: {
            footer_keyword: layout.lineItem.footerKeywords,
            footer_boundary: 0,
          },
          column: {
            column_boundary: layout.lineItem.columnBoundary,
          },
          lineitem: (() => {
            const selectedCol = layout.lineItem.selectedColumn || "column_1";
            const m = selectedCol.match(/column_(\d+)/i);
            const idx = m ? parseInt(m[1], 10) - 1 : 0;
            const colKeywords =
              layout.lineItem.headerKeywords[idx] ?? [];
            const indexColumnKeyword =
              colKeywords.filter(Boolean).join(", ") || selectedCol;
            return {
              index_column: `column ${idx + 1}`,
              index_column_keyword: indexColumnKeyword,
            };
          })(),
          remove_invalid_lineitem: false,
        },
        line_item_rules: [
          {
            header_identification:
              layout.lineItem.headerIdentification || "keyword",
            footer_identification:
              layout.lineItem.footerIdentification || "keyword",
            column_segmentation:
              layout.lineItem.columnSegmentation || "keyword",
            line_item_splitting:
              layout.lineItem.lineItemSplitting || "rule",
          },
        ],
        multitable_layout_id: [],
      };
      return ocrApi.verificationRetriggerLineItem({
        processId,
        layoutRule,
      });
    },
    onSuccess: async () => {
      toast.success("Retrigger successful");
      detailQuery.refetch();
      // Wait for the parent's refetches (projection + coordinates) to
      // resolve before closing the dialog. Otherwise the reviewer lands
      // on the results view while it's still showing the old data — the
      // refetch completes a moment later and the panel flickers.
      // `Promise.resolve` adapts both sync and async return shapes.
      try {
        await Promise.resolve(onRetriggerSuccess?.());
      } catch {
        /* parent refetch errors surface via the cockpit's own query
         * error boundaries; we still want to close on retrigger
         * success even if a follow-up refetch hiccups. */
      }
      // Close the Edit Layout panel and return the reviewer to the
      // results view (now populated with the refreshed data).
      onCancel();
    },
    onError: (err) =>
      toast.error("Retrigger failed", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  const handleTabChange = (next: RulesViewerTab) => {
    setTab(next);
    onTabChange?.(next);
  };

  const toggleField = (id: number) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const updateField = <K extends keyof FieldItem>(
    id: number,
    key: K,
    value: FieldItem[K],
  ) => {
    setFieldItems((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: value } : f)),
    );
  };

  const updateCoord = (
    id: number,
    which: "value_coordinates" | "key_coordinates",
    coord: keyof Coordinates,
    value: string,
  ) => {
    setFieldItems((prev) =>
      prev.map((f) =>
        f.id === id
          ? { ...f, [which]: { ...f[which], [coord]: value } }
          : f,
      ),
    );
  };

  const addKeyword = (id: number) => {
    setFieldItems((prev) =>
      prev.map((f) =>
        f.id === id ? { ...f, keywords: [...f.keywords, ""] } : f,
      ),
    );
  };

  const updateKeyword = (id: number, index: number, value: string) => {
    setFieldItems((prev) =>
      prev.map((f) =>
        f.id === id
          ? {
              ...f,
              keywords: f.keywords.map((k, i) => (i === index ? value : k)),
            }
          : f,
      ),
    );
  };

  const removeKeyword = (id: number, index: number) => {
    setFieldItems((prev) =>
      prev.map((f) =>
        f.id === id
          ? { ...f, keywords: f.keywords.filter((_, i) => i !== index) }
          : f,
      ),
    );
  };

  const removeFieldItem = (id: number) => {
    setFieldItems((prev) => prev.filter((f) => f.id !== id));
  };

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Header */}
      <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-5 py-3">
        <h2 className="text-base font-semibold text-gray-900">
          Edit layout
          {layout.layout_name ? (
            <span className="text-gray-500"> - {layout.layout_name}</span>
          ) : null}
        </h2>
      </div>

      {/* Body — scroll area for the form. */}
      <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
        {detailQuery.isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading layout rules…
          </div>
        ) : detailQuery.isError ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-sm text-rose-600">
            <AlertTriangle className="h-5 w-5" />
            <span>Failed to load layout rules.</span>
            <button
              type="button"
              onClick={() => detailQuery.refetch()}
              className="rounded border border-rose-200 bg-white px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Layout metadata grid — Key/Value table style. */}
            <div className="mb-5 overflow-hidden rounded-md border border-gray-200">
              <div className="grid grid-cols-[120px_1fr] border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                <div className="px-3 py-2">Key</div>
                <div className="px-3 py-2">Value</div>
              </div>
              <MetaRow label="layout_id">
                <input
                  type="text"
                  value={layout.layout_id}
                  onChange={(e) =>
                    setLayout({ ...layout, layout_id: e.target.value })
                  }
                  className="w-full rounded border border-gray-200 bg-white px-2.5 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </MetaRow>
              <MetaRow label="layout_name">
                <input
                  type="text"
                  value={layout.layout_name}
                  onChange={(e) =>
                    setLayout({ ...layout, layout_name: e.target.value })
                  }
                  className="w-full rounded border border-gray-200 bg-white px-2.5 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </MetaRow>
              <MetaRow label="workflow_id" lastRow>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <select
                      value={layout.workflow_id}
                      onChange={(e) =>
                        setLayout({ ...layout, workflow_id: e.target.value })
                      }
                      className="w-full appearance-none rounded border border-gray-200 bg-white px-2.5 py-1.5 pr-8 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select workflow</option>
                      {workflowOptions.map((wf) => (
                        <option key={wf} value={wf}>
                          {wf}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  </div>
                  <button
                    type="button"
                    className="rounded border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-50"
                    title="Edit workflow"
                    onClick={() => {
                      if (!layout.layout_id) {
                        toast.warning("Layout ID is required to edit workflow.");
                        return;
                      }
                      setWorkflowModalOpen(true);
                    }}
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </MetaRow>
            </div>

            {/* Tabs — Line Item / Field Item. */}
            <div className="mb-3 flex border-b border-gray-200">
              {(
                [
                  { key: "lineitem", label: "Line Item" },
                  { key: "fielditem", label: "Field Item" },
                ] as const
              ).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => handleTabChange(t.key)}
                  className={`relative -mb-px flex-1 px-3 pb-2 pt-1 text-xs font-semibold transition-colors ${
                    tab === t.key
                      ? "text-indigo-700"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {t.label}
                  <span
                    className={`absolute inset-x-3 bottom-0 h-0.5 rounded-full transition-colors ${
                      tab === t.key ? "bg-indigo-600" : "bg-transparent"
                    }`}
                  />
                </button>
              ))}
            </div>

            {tab === "fielditem" ? (
              <FieldItemsForm
                fieldItems={fieldItems}
                expanded={expanded}
                onToggle={toggleField}
                onRemove={removeFieldItem}
                onUpdateField={updateField}
                onUpdateCoord={updateCoord}
                onAddKeyword={addKeyword}
                onUpdateKeyword={updateKeyword}
                onRemoveKeyword={removeKeyword}
                extractionType={layout.field_extraction_type}
              />
            ) : (
              <LineItemsForm
                extractionType={layout.lineitem_extraction_type}
                onExtractionTypeChange={(v) =>
                  setLayout((prev) => ({
                    ...prev,
                    lineitem_extraction_type: v,
                  }))
                }
                userPrompt={layout.llm.lineitem_config.user_prompt}
                onUserPromptChange={(v) =>
                  setLayout((prev) => ({
                    ...prev,
                    llm: {
                      ...prev.llm,
                      lineitem_config: {
                        ...prev.llm.lineitem_config,
                        user_prompt: v,
                      },
                    },
                  }))
                }
                lineItem={layout.lineItem}
                onLineItemChange={(next) =>
                  setLayout((prev) => ({ ...prev, lineItem: next }))
                }
                ruleOptions={ruleOptions}
              />
            )}
          </>
        )}
      </div>

      {/* Footer — Cancel / Retrigger. Sticks to the bottom of the panel:
          flex-shrink-0 keeps it out of the body's overflow-auto region. */}
      <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-white px-5 py-2 shadow-[0_-2px_8px_-4px_rgba(0,0,0,0.08)]">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 bg-white px-5 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => retriggerMutation.mutate()}
          disabled={retriggerMutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
        >
          {retriggerMutation.isPending ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Retriggering…
            </>
          ) : (
            "Retrigger"
          )}
        </button>
      </div>

      {/* Workflow assignment modal — invoked from the edit icon next to
          workflow_id, mirrors ocr-ui's EditWorkflow page. */}
      <WorkflowAssignModal
        open={workflowModalOpen}
        layoutId={layout.layout_id}
        initialWorkflowId={layout.workflow_id}
        onClose={() => setWorkflowModalOpen(false)}
        onApplied={(workflowId) =>
          setLayout((prev) => ({ ...prev, workflow_id: workflowId }))
        }
      />
    </div>
  );
}

function MetaRow({
  label,
  children,
  lastRow,
}: {
  label: string;
  children: React.ReactNode;
  lastRow?: boolean;
}) {
  return (
    <div
      className={`grid grid-cols-[120px_1fr] items-center bg-white ${
        lastRow ? "" : "border-b border-gray-100"
      }`}
    >
      <div className="px-3 py-2 text-xs font-medium text-gray-700">{label}</div>
      <div className="px-3 py-2">{children}</div>
    </div>
  );
}

interface FieldItemsFormProps {
  fieldItems: FieldItem[];
  expanded: Record<number, boolean>;
  onToggle: (id: number) => void;
  onRemove: (id: number) => void;
  onUpdateField: <K extends keyof FieldItem>(
    id: number,
    key: K,
    value: FieldItem[K],
  ) => void;
  onUpdateCoord: (
    id: number,
    which: "value_coordinates" | "key_coordinates",
    coord: keyof Coordinates,
    value: string,
  ) => void;
  onAddKeyword: (id: number) => void;
  onUpdateKeyword: (id: number, index: number, value: string) => void;
  onRemoveKeyword: (id: number, index: number) => void;
  extractionType: "rules" | "llm";
}

function FieldItemsForm({
  fieldItems,
  expanded,
  onToggle,
  onRemove,
  onUpdateField,
  onUpdateCoord,
  onAddKeyword,
  onUpdateKeyword,
  onRemoveKeyword,
  extractionType,
}: FieldItemsFormProps) {
  return (
    <div className="space-y-3">
      {/* Extraction Type strip. */}
      <div className="rounded-md border border-gray-200 px-3 py-2.5">
        <div className="grid grid-cols-[120px_1fr] items-center">
          <label className="text-xs font-bold text-gray-700">
            Extraction Type
          </label>
          <input
            value={extractionType}
            readOnly
            className="w-full rounded border border-gray-200 bg-gray-100 px-2.5 py-1.5 text-xs text-gray-700"
          />
        </div>
      </div>

      {fieldItems.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-200 px-3 py-8 text-center text-xs text-gray-400">
          No field rules configured for this layout.
        </div>
      ) : (
        fieldItems.map((field, idx) => {
          const isOpen = expanded[field.id] ?? false;
          return (
            <div
              key={field.id}
              className="overflow-hidden rounded-md border border-gray-200"
            >
              <div
                className="flex cursor-pointer items-center justify-between bg-gray-200 px-3 py-2 hover:bg-gray-300"
                onClick={() => onToggle(field.id)}
              >
                <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  )}
                  <span>Field {idx + 1}</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(field.id);
                  }}
                  className="rounded border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-rose-50 hover:text-rose-600"
                  title="Delete field"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              {isOpen ? (
                <div className="space-y-3 bg-white px-3 py-3">
                  <FieldRow label="Field name">
                    <input
                      type="text"
                      value={field.field_name}
                      onChange={(e) =>
                        onUpdateField(field.id, "field_name", e.target.value)
                      }
                      className="w-full rounded border-0 bg-transparent px-0 py-1 text-xs font-bold text-gray-900 focus:outline-none"
                    />
                  </FieldRow>
                  <FieldRow label="Page Number">
                    <input
                      type="text"
                      value={field.pageNumber}
                      onChange={(e) =>
                        onUpdateField(field.id, "pageNumber", e.target.value)
                      }
                      className="w-full rounded border border-gray-200 bg-white px-2.5 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </FieldRow>
                  <FieldRow label="Boundary Type">
                    <div className="relative">
                      <select
                        value={field.boundaryType}
                        onChange={(e) =>
                          onUpdateField(
                            field.id,
                            "boundaryType",
                            e.target.value,
                          )
                        }
                        className="w-full appearance-none rounded border border-gray-200 bg-white px-2.5 py-1.5 pr-8 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        {FIELD_BOUNDARY_TYPES.map((b) => (
                          <option key={b.value} value={b.value}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                    </div>
                  </FieldRow>
                  <FieldRow label="Keywords">
                    <KeywordEditor
                      keywords={field.keywords}
                      onChange={(idx, val) => onUpdateKeyword(field.id, idx, val)}
                      onRemove={(idx) => onRemoveKeyword(field.id, idx)}
                      onAdd={() => onAddKeyword(field.id)}
                    />
                  </FieldRow>

                  <CoordsBlock
                    label="Value Coordinates"
                    coords={field.value_coordinates}
                    onChange={(coord, val) =>
                      onUpdateCoord(field.id, "value_coordinates", coord, val)
                    }
                  />
                  <CoordsBlock
                    label="Key Coordinates"
                    coords={field.key_coordinates}
                    onChange={(coord, val) =>
                      onUpdateCoord(field.id, "key_coordinates", coord, val)
                    }
                  />
                </div>
              ) : null}
            </div>
          );
        })
      )}
    </div>
  );
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-start gap-3">
      <label className="pt-1.5 text-xs font-bold text-gray-700">{label}</label>
      <div>{children}</div>
    </div>
  );
}

function KeywordEditor({
  keywords,
  onChange,
  onRemove,
  onAdd,
}: {
  keywords: string[];
  onChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {keywords.map((kw, i) => (
        <div
          key={i}
          className="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs"
        >
          <input
            value={kw}
            placeholder="Keyword"
            onChange={(e) => onChange(i, e.target.value)}
            className="w-24 bg-transparent text-xs focus:outline-none"
          />
          <button
            type="button"
            onClick={() => onRemove(i)}
            className="rounded p-0.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600"
            aria-label="Remove keyword"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
        aria-label="Add keyword"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function CoordsBlock({
  label,
  coords,
  onChange,
}: {
  label: string;
  coords: Coordinates;
  onChange: (coord: keyof Coordinates, value: string) => void;
}) {
  const order: Array<keyof Coordinates> = [
    "xmin",
    "ymin",
    "xmax",
    "ymax",
    "xDif",
    "yDif",
  ];
  return (
    <div>
      <div className="mb-1.5 text-xs font-bold text-gray-700">{label}</div>
      <div className="grid grid-cols-4 gap-2">
        {order.map((k) => (
          <div
            key={k}
            className="flex min-w-0 items-center justify-between gap-1 rounded border border-gray-200 bg-white px-2 py-1.5"
          >
            <input
              value={coords[k]}
              placeholder="0"
              onChange={(e) => onChange(k, e.target.value)}
              className="w-full min-w-0 bg-transparent text-xs text-gray-900 focus:outline-none"
            />
            <span className="flex-shrink-0 text-[10px] tracking-wider text-gray-400">
              {k.toLowerCase()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface RuleDropdownOptions {
  headerIdentification: { label: string; value: string }[];
  footerIdentification: { label: string; value: string }[];
  columnSegmentation: { label: string; value: string }[];
  lineItemSplitting: { label: string; value: string }[];
}

function LineItemsForm({
  extractionType,
  onExtractionTypeChange,
  userPrompt,
  onUserPromptChange,
  lineItem,
  onLineItemChange,
  ruleOptions,
}: {
  extractionType: "rules" | "llm";
  onExtractionTypeChange?: (value: "rules" | "llm") => void;
  userPrompt: string;
  onUserPromptChange?: (value: string) => void;
  lineItem: LineItemRule;
  onLineItemChange?: (next: LineItemRule) => void;
  ruleOptions?: RuleDropdownOptions;
}) {
  const [tableOpen, setTableOpen] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const update = <K extends keyof LineItemRule>(
    key: K,
    value: LineItemRule[K],
  ) => onLineItemChange?.({ ...lineItem, [key]: value });

  const updateColumnBoundary = (
    colName: string,
    field: keyof ColumnBoundary,
    value: string,
  ) =>
    onLineItemChange?.({
      ...lineItem,
      columnBoundary: {
        ...lineItem.columnBoundary,
        [colName]: {
          ...(lineItem.columnBoundary[colName] ?? {
            xmin: "",
            xmax: "",
            ymin: "",
            ymax: "",
          }),
          [field]: value,
        },
      },
    });

  const deleteTable = () =>
    onLineItemChange?.({ ...lineItem, columnBoundary: {} });

  const selectedColumnOptions = lineItem.headerKeywords.map(
    (kws, i) => {
      const joined = (kws || []).filter(Boolean).join(", ");
      const label = joined || `Column ${i + 1}`;
      return {
        label: label.length > 30 ? label.slice(0, 30) + "…" : label,
        value: `column_${i + 1}`,
      };
    },
  );

  const columnEntries = Object.entries(lineItem.columnBoundary);

  return (
    <div className="space-y-3">
      {/* Extraction Type */}
      <div className="rounded-md border border-gray-200 px-3 py-2.5">
        <div className="grid grid-cols-[120px_1fr] items-center">
          <label className="text-xs font-bold text-gray-700">
            Extraction Type
          </label>
          <div className="relative">
            <select
              value={extractionType}
              onChange={(e) =>
                onExtractionTypeChange?.(e.target.value as "rules" | "llm")
              }
              className="w-full appearance-none rounded border border-gray-200 bg-white px-2.5 py-1.5 pr-8 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select an option</option>
              <option value="rules">Rules</option>
              <option value="llm">LLM</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          </div>
        </div>
      </div>

      {extractionType === "llm" ? (
        <div className="space-y-2 rounded-md border border-gray-200 bg-white p-3">
          <label className="block text-sm font-bold text-gray-700">
            Prompt
          </label>
          <textarea
            value={userPrompt}
            onChange={(e) => onUserPromptChange?.(e.target.value)}
            placeholder="Enter prompt for line item extraction..."
            rows={5}
            className="w-full resize-y rounded border border-gray-200 bg-white p-3 text-xs text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      ) : (
        /* Rules mode — Table 1 collapsible with column boundaries,
           splitting index, and Advanced section. Mirrors ocr-ui's
           LineItemsTab structure. */
        <div className="overflow-hidden rounded-md border border-gray-200">
          {/* Table 1 header */}
          <div
            className="flex cursor-pointer items-center justify-between bg-gray-200 px-3 py-2 hover:bg-gray-300"
            onClick={() => setTableOpen((v) => !v)}
          >
            <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
              {tableOpen ? (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              )}
              <span>Table 1</span>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                deleteTable();
              }}
              className="rounded border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-rose-50 hover:text-rose-600"
              title="Delete table"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>

          {tableOpen ? (
            <div className="space-y-3 bg-gray-50/40 p-3">
              {/* Column Segmentation — merge backend options with the
                  local fallback so "keyword" is always present and the
                  default value resolves to a visible option. Without
                  this, a backend rule_delimiter_list that omits
                  "keyword" would leave the dropdown blank even though
                  state holds the default. */}
              <SmallSelectRow
                label="Column Segmentation"
                value={lineItem.columnSegmentation}
                onChange={(v) => update("columnSegmentation", v)}
                options={mergeOptions(
                  ruleOptions?.columnSegmentation,
                  COLUMN_SEGMENTATION_OPTIONS,
                )}
              />

              {/* Column boundary rows — only when segmentation is rule. */}
              {lineItem.columnSegmentation === "rule" ? (
                columnEntries.length === 0 ? (
                  <div className="rounded border border-dashed border-gray-200 px-3 py-4 text-center text-xs text-gray-400">
                    No columns configured.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {columnEntries.map(([colName, bounds]) => (
                      <div
                        key={colName}
                        className="grid grid-cols-[1fr_80px_80px] items-center gap-2"
                      >
                        <div
                          className="truncate text-xs font-semibold text-gray-700"
                          title={colName}
                        >
                          {colName}
                        </div>
                        <CoordInput
                          value={bounds.xmin}
                          onChange={(v) =>
                            updateColumnBoundary(colName, "xmin", v)
                          }
                          label="xmin"
                        />
                        <CoordInput
                          value={bounds.xmax}
                          onChange={(v) =>
                            updateColumnBoundary(colName, "xmax", v)
                          }
                          label="xmax"
                        />
                      </div>
                    ))}
                  </div>
                )
              ) : null}

              {/* Lineitem Splitting Index — same merge so "index" is
                  always selectable as the default. */}
              <SmallSelectRow
                label="Lineitem Splitting Index"
                value={lineItem.lineItemSplitting}
                onChange={(v) => update("lineItemSplitting", v)}
                options={mergeOptions(
                  ruleOptions?.lineItemSplitting,
                  LINEITEM_SPLITTING_OPTIONS,
                )}
              />

              {/* Selected Column — only when splitting === "index" */}
              {lineItem.lineItemSplitting === "index" ? (
                <div className="grid grid-cols-[140px_1fr] items-center gap-2">
                  <label className="text-xs font-bold text-gray-700">
                    Selected Column
                  </label>
                  <div className="relative">
                    <select
                      value={lineItem.selectedColumn}
                      onChange={(e) =>
                        update("selectedColumn", e.target.value)
                      }
                      className="w-full appearance-none rounded border border-gray-200 bg-white px-2.5 py-1.5 pr-8 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select a column</option>
                      {selectedColumnOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Advanced */}
          <div
            className="flex cursor-pointer items-center gap-1.5 border-t border-gray-200 bg-gray-200 px-3 py-2 hover:bg-gray-300"
            onClick={() => setAdvancedOpen((v) => !v)}
          >
            {advancedOpen ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
            <span className="text-sm font-semibold text-gray-800">
              Advanced
            </span>
          </div>
          {advancedOpen ? (
            <div className="space-y-3 bg-gray-50/40 p-3">
              {/* Header section */}
              <SmallSelectRow
                label="Header Identification"
                value={lineItem.headerIdentification}
                onChange={(v) => update("headerIdentification", v)}
                options={
                  ruleOptions?.headerIdentification?.length
                    ? ruleOptions.headerIdentification
                    : IDENTIFICATION_OPTIONS
                }
              />
              <div>
                <div className="mb-2 text-xs font-bold text-gray-700">
                  Header{" "}
                  {lineItem.headerIdentification === "keyword"
                    ? "Keyword"
                    : "Pattern"}
                </div>
                <KeywordColumnsEditor
                  columns={lineItem.headerKeywords}
                  onChange={(next) => update("headerKeywords", next)}
                  placeholder={
                    lineItem.headerIdentification === "keyword"
                      ? "Keyword"
                      : "Pattern"
                  }
                />
              </div>

              {/* Footer section */}
              <SmallSelectRow
                label="Footer Identification"
                value={lineItem.footerIdentification}
                onChange={(v) => update("footerIdentification", v)}
                options={
                  ruleOptions?.footerIdentification?.length
                    ? ruleOptions.footerIdentification
                    : IDENTIFICATION_OPTIONS
                }
              />
              <div>
                <div className="mb-2 text-xs font-bold text-gray-700">
                  Footer{" "}
                  {lineItem.footerIdentification === "keyword"
                    ? "Keyword"
                    : "Pattern"}
                </div>
                <KeywordTagList
                  keywords={lineItem.footerKeywords}
                  onChange={(next) => update("footerKeywords", next)}
                  placeholder={
                    lineItem.footerIdentification === "keyword"
                      ? "Keyword"
                      : "Pattern"
                  }
                />
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function CoordInput({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  label: string;
}) {
  return (
    <div className="flex items-center justify-between rounded border border-gray-200 bg-white px-2 py-1.5">
      <input
        value={value}
        placeholder="0"
        onChange={(e) => onChange(e.target.value)}
        className="w-full min-w-0 bg-transparent text-xs text-gray-900 focus:outline-none"
      />
      <span className="flex-shrink-0 text-[10px] tracking-wider text-gray-400">
        {label}
      </span>
    </div>
  );
}

function SmallSelectRow({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: { label: string; value: string }[];
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-center gap-2">
      <label className="text-xs font-bold text-gray-700">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded border border-gray-200 bg-white px-2.5 py-1.5 pr-8 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
      </div>
    </div>
  );
}

/**
 * Editable list of keyword tags. Click a tag to edit, X to delete,
 * + to add a new blank one. Mirrors ocr-ui's KeywordTag flow.
 */
function KeywordTagList({
  keywords,
  onChange,
  placeholder = "Keyword",
}: {
  keywords: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  const update = (idx: number, value: string) =>
    onChange(keywords.map((k, i) => (i === idx ? value : k)));
  const remove = (idx: number) =>
    onChange(keywords.filter((_, i) => i !== idx));
  const add = () => {
    onChange([...keywords, ""]);
    setEditingIdx(keywords.length);
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {keywords.map((kw, i) =>
        editingIdx === i ? (
          <input
            key={i}
            autoFocus
            value={kw}
            placeholder={placeholder}
            onChange={(e) => update(i, e.target.value)}
            onBlur={() => {
              if (!kw.trim()) remove(i);
              setEditingIdx(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              if (e.key === "Escape") {
                if (!kw.trim()) remove(i);
                setEditingIdx(null);
              }
            }}
            className="w-24 rounded border border-blue-300 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        ) : (
          <span
            key={i}
            onClick={() => setEditingIdx(i)}
            className="inline-flex cursor-text items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800 hover:border-gray-300"
            title="Click to edit"
          >
            {kw || <span className="italic text-gray-400">{placeholder}</span>}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove(i);
              }}
              className="rounded p-0.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600"
              aria-label="Remove keyword"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ),
      )}
      <button
        type="button"
        onClick={add}
        className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
        aria-label="Add keyword"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/**
 * Per-column keyword editor — one row per column with its own
 * KeywordTagList. Column rows have +/trash buttons to insert / delete
 * the column. Mirrors ocr-ui's Header Keyword section structure.
 */
function KeywordColumnsEditor({
  columns,
  onChange,
  placeholder,
}: {
  columns: string[][];
  onChange: (next: string[][]) => void;
  placeholder?: string;
}) {
  const setColumn = (idx: number, next: string[]) =>
    onChange(columns.map((c, i) => (i === idx ? next : c)));
  const insertAfter = (idx: number) => {
    const next = [...columns];
    next.splice(idx + 1, 0, []);
    onChange(next);
  };
  const removeCol = (idx: number) =>
    onChange(columns.filter((_, i) => i !== idx));
  const addFirst = () => onChange([[]]);

  if (columns.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={addFirst}
          className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
          aria-label="Add column"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <span className="text-xs italic text-gray-400">No columns</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {columns.map((kws, i) => (
        <div
          key={i}
          className="space-y-1.5 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-gray-500">
              Column {i + 1}
            </span>
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => insertAfter(i)}
                className="rounded p-1 text-blue-600 hover:bg-blue-50"
                title={`Add column after Column ${i + 1}`}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => removeCol(i)}
                className="rounded p-1 text-gray-400 hover:bg-rose-50 hover:text-rose-600"
                title={`Delete Column ${i + 1}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <KeywordTagList
            keywords={kws}
            onChange={(next) => setColumn(i, next)}
            placeholder={placeholder}
          />
        </div>
      ))}
    </div>
  );
}
